import api from './api';

/**
 * 📊 Servicio de Logging y Auditoría
 * Maneja todas las operaciones relacionadas con logs y auditoría del sistema
 */
class LoggingService {
  
  // ================================
  // HISTORIAL DE ENTIDADES
  // ================================

  /**
   * Obtener historial completo de una orden de inserción
   * @param {number} orderId - ID de la orden
   * @returns {Promise<Object>} Historial de la orden
   */
  async getInsertionOrderHistory(orderId) {
    try {
      const response = await api.get(`/budget/insertion-orders/${orderId}/history`);
      
      // Procesar respuesta del backend
      const processedData = {
        success: true,
        data: {
          orderId: response.data.data?.orderId || orderId,
          totalLogs: response.data.data?.totalLogs || 0,
          logs: response.data.data?.logs || []
        }
      };

      return processedData;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener historial de la orden'
      };
    }
  }

  /**
   * Obtener historial completo de una asignación
   * @param {number} assignmentId - ID de la asignación
   * @returns {Promise<Object>} Historial de la asignación
   */
  async getAssignmentHistory(assignmentId) {
    try {
      const response = await api.get(`/budget/assignments/${assignmentId}/history`);
      
      // Acceder correctamente a los datos anidados
      const responseData = response.data.data || response.data;
      
      const processedData = {
        success: true,
        data: {
          assignmentId: responseData.assignmentId || assignmentId,
          totalLogs: responseData.totalLogs || 0,
          logs: responseData.logs || []
        }
      };

      return processedData;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener historial de la asignación'
      };
    }
  }

  // ================================
  // ACTIVIDAD Y ESTADÍSTICAS
  // ================================

  /**
   * Obtener actividad reciente en presupuestos
   * @param {number} limit - Límite de logs a obtener (default: 20)
   * @returns {Promise<Object>} Actividad reciente
   */
  async getRecentBudgetActivity(limit = 20) {
    try {
      const response = await api.get(`/budget/recent-activity?limit=${limit}`);
      return {
        success: true,
        data: response.data,
        totalLogs: response.data.totalLogs,
        logs: response.data.logs
      };
    } catch (error) {
      console.error('Error obteniendo actividad reciente:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener actividad reciente',
        data: null
      };
    }
  }

  /**
   * Obtener estadísticas detalladas de actividad
   * @param {number} days - Días hacia atrás para calcular (default: 30)
   * @returns {Promise<Object>} Estadísticas de actividad
   */
  async getBudgetActivityStats(days = 30) {
    try {
      const response = await api.get(`/budget/activity-stats?days=${days}`);
      return {
        success: true,
        data: response.data,
        stats: {
          totalActions: response.data.totalActions,
          period: response.data.period,
          byAction: response.data.byAction,
          byEntity: response.data.byEntity,
          byUser: response.data.byUser,
          dailyActivity: response.data.dailyActivity,
          mostActiveEntities: response.data.mostActiveEntities
        }
      };
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener estadísticas',
        data: null
      };
    }
  }

  // ================================
  // LOGS GENERALES
  // ================================

  /**
   * Obtener logs recientes del sistema
   * @param {number} limit - Límite de logs (default: 50)
   * @returns {Promise<Object>} Logs recientes
   */
  async getRecentLogs(limit = 50) {
    try {
      const response = await api.get(`/logs/recent?limit=${limit}`);
      return {
        success: true,
        data: response.data,
        logs: response.data
      };
    } catch (error) {
      console.error('Error obteniendo logs recientes:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener logs recientes',
        data: null
      };
    }
  }

  /**
   * Obtener logs de una entidad específica
   * @param {string} entityType - Tipo de entidad (INSERTION_ORDER, BUDGET_ASSIGNMENT, etc.)
   * @param {number} entityId - ID de la entidad
   * @returns {Promise<Object>} Logs de la entidad
   */
  async getEntityLogs(entityType, entityId) {
    try {
      const response = await api.get(`/logs/entity/${entityType}/${entityId}`);
      return {
        success: true,
        data: response.data,
        logs: response.data
      };
    } catch (error) {
      console.error('Error obteniendo logs de entidad:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener logs de la entidad',
        data: null
      };
    }
  }

  // ================================
  // UTILIDADES PARA FRONTEND
  // ================================

  /**
   * Formatear log para mostrar en UI
   * @param {Object} log - Log object
   * @returns {Object} Log formateado
   */
  formatLogForUI(log) {
    return {
      id: log.id,
      action: this.getActionLabel(log.action),
      actionIcon: this.getActionIcon(log.action),
      actionColor: this.getActionColor(log.action),
      entity: this.getEntityLabel(log.entityType),
      entityId: log.entityId,
      description: log.description,
      user: {
        name: log.userName,
        email: log.userEmail,
        initials: this.getUserInitials(log.userName)
      },
      timestamp: log.createdAt,
      timeAgo: this.getTimeAgo(log.createdAt),
      changes: this.formatChanges(log),
      summary: log.summary || this.generateSummary(log)
    };
  }

  /**
   * Obtener etiqueta legible para acción
   */
  getActionLabel(action) {
    const labels = {
      'CREATE': 'Creado',
      'UPDATE': 'Modificado',
      'DELETE': 'Eliminado',
      'VIEW': 'Visualizado',
      'EXPORT': 'Exportado'
    };
    return labels[action] || action;
  }

  /**
   * Obtener ícono para acción
   */
  getActionIcon(action) {
    const icons = {
      'CREATE': 'plus-circle',
      'UPDATE': 'edit',
      'DELETE': 'trash-2',
      'VIEW': 'eye',
      'EXPORT': 'download'
    };
    return icons[action] || 'activity';
  }

  /**
   * Obtener color para acción
   */
  getActionColor(action) {
    const colors = {
      'CREATE': 'success',
      'UPDATE': 'warning',
      'DELETE': 'danger',
      'VIEW': 'info',
      'EXPORT': 'primary'
    };
    return colors[action] || 'secondary';
  }

  /**
   * Obtener etiqueta legible para entidad
   */
  getEntityLabel(entityType) {
    const labels = {
      'INSERTION_ORDER': 'Orden de Inserción',
      'BUDGET_ASSIGNMENT': 'Asignación',
      'USER': 'Usuario',
      'ROLE': 'Rol',
      'CLIENT': 'Cliente',
      'ACCOUNT': 'Cuenta'
    };
    return labels[entityType] || entityType;
  }

  /**
   * Obtener iniciales del usuario
   */
  getUserInitials(userName) {
    if (!userName) return 'U';
    return userName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  /**
   * Calcular tiempo transcurrido
   */
  getTimeAgo(timestamp) {
    const now = new Date();
    const logTime = new Date(timestamp);
    const diffMs = now - logTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    
    return logTime.toLocaleDateString('es-ES');
  }

  /**
   * Formatear cambios para mostrar diff
   */
  formatChanges(log) {
    if (!log.changedFields || !log.oldValues || !log.newValues) {
      return [];
    }

    return log.changedFields.map(field => ({
      field: this.getFieldLabel(field),
      fieldKey: field,
      oldValue: log.oldValues[field],
      newValue: log.newValues[field],
      oldFormatted: this.formatValue(field, log.oldValues[field]),
      newFormatted: this.formatValue(field, log.newValues[field])
    }));
  }

  /**
   * Obtener etiqueta legible para campo
   */
  getFieldLabel(field) {
    const labels = {
      'clientName': 'Cliente',
      'businessUnit': 'Unidad de Negocio',
      'grossInvestment': 'Inversión Bruta',
      'netInvestment': 'Inversión Neta',
      'brokerFee': 'Fee Broker',
      'mediaFee': 'Fee Media',
      'adminFee': 'Fee Administrativo',
      'dateFrom': 'Fecha Inicio',
      'dateTo': 'Fecha Fin',
      'currency': 'Moneda',
      'description': 'Descripción',
      'montoValor': 'Monto',
      'isActive': 'Estado'
    };
    return labels[field] || field;
  }

  /**
   * Formatear valor según el tipo de campo
   */
  formatValue(field, value) {
    if (value === null || value === undefined) return 'N/A';

    // Campos monetarios
    if (['grossInvestment', 'netInvestment', 'brokerFee', 'mediaFee', 'adminFee', 'montoValor'].includes(field)) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0
      }).format(value);
    }

    // Fechas
    if (['dateFrom', 'dateTo', 'createdAt', 'updatedAt'].includes(field)) {
      return new Date(value).toLocaleDateString('es-ES');
    }

    // Booleanos
    if (field === 'isActive') {
      return value ? 'Activo' : 'Inactivo';
    }

    return String(value);
  }

  /**
   * Generar resumen automático del log
   */
  generateSummary(log) {
    const entity = this.getEntityLabel(log.entityType);
    const action = this.getActionLabel(log.action);
    return `${log.userName} ${action.toLowerCase()} ${entity} #${log.entityId}`;
  }

  // ================================
  // FILTROS Y BÚSQUEDA
  // ================================

  /**
   * Filtrar logs por criterios
   * @param {Array} logs - Array de logs
   * @param {Object} filters - Filtros a aplicar
   * @returns {Array} Logs filtrados
   */
  filterLogs(logs, filters = {}) {
    let filtered = [...logs];

    // Filtrar por usuario
    if (filters.userId) {
      filtered = filtered.filter(log => log.userId === filters.userId);
    }

    // Filtrar por acción
    if (filters.action) {
      filtered = filtered.filter(log => log.action === filters.action);
    }

    // Filtrar por tipo de entidad
    if (filters.entityType) {
      filtered = filtered.filter(log => log.entityType === filters.entityType);
    }

    // Filtrar por rango de fechas
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(log => new Date(log.createdAt) >= fromDate);
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filtered = filtered.filter(log => new Date(log.createdAt) <= toDate);
    }

    // Filtrar por texto en descripción
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(log => 
        log.description?.toLowerCase().includes(searchLower) ||
        log.userName?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }

  /**
   * Agrupar logs por fecha
   * @param {Array} logs - Array de logs
   * @returns {Object} Logs agrupados por fecha
   */
  groupLogsByDate(logs) {
    const grouped = {};
    
    logs.forEach(log => {
      const date = new Date(log.createdAt).toLocaleDateString('es-ES');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(this.formatLogForUI(log));
    });

    return grouped;
  }
}

// Exportar instancia singleton
const loggingService = new LoggingService();
export default loggingService; 